import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import classes from './glossary.module.scss';
import classes2 from '../files/styles.module.scss';
import { createCn } from '../../helpers/utils';
import {
  ApiError,
  apiService,
  GlossaryTermCreatePayload,
  GlossaryTermItem,
} from '../../services/api.service';
import GlossaryToolbar, { ActiveFilter, LanguageFilter } from './components/glossary-toolbar.component';
import GlossaryList from './components/glossary-list.component';
import GlossaryForm from './components/glossary-form.component';

const cn = createCn('glossary', classes);
const cn2 = createCn('files', classes2);

const GlossaryPage = () => {
  const { t } = useTranslation();

  const [terms, setTerms] = useState<GlossaryTermItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [forbidden, setForbidden] = useState(false);

  const [search, setSearch] = useState('');
  const [languageFilter, setLanguageFilter] = useState<LanguageFilter>('all');
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('all');

  const [formOpen, setFormOpen] = useState(false);
  const [editingTerm, setEditingTerm] = useState<GlossaryTermItem | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    setForbidden(false);
    try {
      const data = await apiService.listGlossary();
      setTerms(data);
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) {
        setForbidden(true);
      } else {
        setLoadError(e instanceof Error ? e.message : t('glossary.error-load'));
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredTerms = useMemo(() => {
    const query = search.trim().toLowerCase();
    return terms.filter((term) => {
      if (languageFilter !== 'all' && term.language !== languageFilter) return false;
      if (activeFilter === 'active' && !term.is_active) return false;
      if (activeFilter === 'inactive' && term.is_active) return false;
      if (!query) return true;
      const haystack = [term.term, term.description || '', ...term.aliases].join(' ').toLowerCase();
      return haystack.includes(query);
    });
  }, [terms, search, languageFilter, activeFilter]);

  const openCreateForm = () => {
    setEditingTerm(null);
    setFormOpen(true);
  };

  const openEditForm = (term: GlossaryTermItem) => {
    setEditingTerm(term);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingTerm(null);
  };

  const handleFormSubmit = async (payload: GlossaryTermCreatePayload) => {
    setSaving(true);
    try {
      if (editingTerm) {
        const updated = await apiService.updateGlossaryTerm(editingTerm.id, payload);
        setTerms((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
        toast.success(t('glossary.toast-updated', { term: updated.term }) as string);
      } else {
        const created = await apiService.createGlossaryTerm(payload);
        setTerms((prev) => [...prev, created]);
        toast.success(t('glossary.toast-created', { term: created.term }) as string);
      }
      closeForm();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : (t('glossary.error-create') as string));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (term: GlossaryTermItem) => {
    if (!window.confirm(t('glossary.delete-confirm', { term: term.term }) as string)) return;

    setDeletingId(term.id);
    try {
      await apiService.deleteGlossaryTerm(term.id);
      setTerms((prev) => prev.filter((item) => item.id !== term.id));
      toast.success(t('glossary.toast-deleted', { term: term.term }) as string);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : (t('glossary.error-delete') as string));
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleActive = async (term: GlossaryTermItem, isActive: boolean) => {
    setTogglingId(term.id);
    try {
      const updated = await apiService.updateGlossaryTerm(term.id, { is_active: isActive });
      setTerms((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      toast.success(
        (isActive ? t('glossary.toast-activated', { term: updated.term }) : t('glossary.toast-deactivated', { term: updated.term })) as string,
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : (t('glossary.error-toggle') as string));
    } finally {
      setTogglingId(null);
    }
  };

  if (forbidden) {
    return (
      <div className={cn2('')}>
        <div className={cn2('heading')}>{t('glossary.title')}</div>
        <div className={cn2('content')}>
          <div className={cn('forbidden')}>
            <div className={cn('forbidden-title')}>{t('glossary.forbidden-title')}</div>
            <div className={cn('forbidden-text')}>{t('glossary.forbidden-text')}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn2('')}>
      <div className={cn2('heading')}>{t('glossary.title')}</div>

      <div className={cn2('content')}>
        <p className={cn('hint')}>{t('glossary.hint')}</p>

        {loading ? (
          <div className={cn('status')}>{t('glossary.status-loading')}</div>
        ) : (
          <>
            {loadError && (
              <div className={cn('error-banner')}>
                {loadError}
                <button type="button" onClick={load}>{t('glossary.error-retry')}</button>
              </div>
            )}

            <GlossaryToolbar
              search={search}
              onSearchChange={setSearch}
              languageFilter={languageFilter}
              onLanguageFilterChange={setLanguageFilter}
              activeFilter={activeFilter}
              onActiveFilterChange={setActiveFilter}
              count={filteredTerms.length}
              onAdd={openCreateForm}
            />

            {formOpen && (
              <GlossaryForm
                term={editingTerm}
                saving={saving}
                onSubmit={handleFormSubmit}
                onCancel={closeForm}
              />
            )}

            <GlossaryList
              terms={filteredTerms}
              hasNoTermsAtAll={terms.length === 0}
              togglingId={togglingId}
              deletingId={deletingId}
              onAdd={openCreateForm}
              onEdit={openEditForm}
              onDelete={handleDelete}
              onToggleActive={handleToggleActive}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default GlossaryPage;
